import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";
import { DocumentThumbnail } from "./DocumentThumbnail";
import { UserDocument } from "../../models/UserDocument";
import { Modes } from "./types";
import { documentsAPI } from "apis";
import { MAX_SMALL_UPLOAD_BYTES } from "../../constants";
import { RenderFileUploadBox } from "./FileUploadArea";
import { DocumentTypeFormControl } from "views/shared/forms/DocumentTypeFormControl";
import { Loading } from "views/shared/Loading";
import { useAuthContext } from "AuthContextWrapper";

interface Props {
  document?: UserDocument;
  documentSrc?: string;
  setMode?: (mode: Modes) => void;
  onCancel?: () => void;
  onClickThumbnail?: () => void;
  onUpdateDoc?: (shouldClose: boolean) => void;
  includeOnlyDocumentTypeIds?: number[],
  documentId?: string | number
  isError?: boolean
  error?: Error
  isLoading?: boolean
  isSuccessful?: boolean
}

export const EditUserDocument = ({
  document,
  documentSrc,
  onCancel,
  onUpdateDoc,
  setMode,
  includeOnlyDocumentTypeIds,
  isLoading,
  isSuccessful
}: Props): JSX.Element => {
  const { activeEstate, user } = useAuthContext()
  const isNew = !document?.id;
  const [title, setTitle] = useState(document?.version?.title || "");
  const [notes, setNotes] = useState(document?.notes || "");
  const [cloudUrl, setCloudUrl] = useState<string>(document?.version?.cloudUrl || "")
  const [documentType, setDocumentType] = useState<number | undefined>(document?.documentTypeId);
  const [uploadedFiles, setUploadedFiles] = useState<FileList | undefined>();
  const [s3Key, setS3Key] = useState<string>("")
  const [progress, setProgress] = useState<number | undefined>();
  const [errors, setErrors] = useState<string[]>([]);
  const [message, setMessage] = useState<string>()
  const [isUploading, setIsUploading] = useState<boolean>(false)

  useEffect(() => {
    if (isNew) {
      return
    }
    if (!uploadedFiles) {
      return
    }

    //Automatically upload / update on file change for edits
    handleSubmit(true)
  }, [uploadedFiles])

  const createFileInDatabase = async (): Promise<number | undefined> => {
    if (!activeEstate?.id || !user?.id) {
      setErrors(["We were unable to create the file in our system, if the problem persists please log out and back in"])
      return
    }

    const doc: UserDocument | null = await documentsAPI.saveDocument(
      activeEstate?.id,
      user?.id,
      title,
      document?.id,
      documentType,
      undefined,
      cloudUrl,
      notes,
      undefined,
      undefined,
      false,
      s3Key,
    )
    console.log("done creating file in db")
    if (doc && onUpdateDoc) {
      setUploadedFiles(undefined);
    }
    setProgress(undefined);

    if (!doc) {
      setErrors(["Sorry, file upload failed.  Please try again."]);
      return
    }

    return doc?.id
  }

  const uploadSmallFile = async (uploadFile: File): Promise<number | undefined> => {
    console.log("uploading small file")
    if (!activeEstate?.id || !user?.id) {
      setErrors(["We were unable to create the file in our system, if the problem persists please log out and back in"])
      return
    }

    const doc = await documentsAPI.saveDocument(
      activeEstate?.id,
      user?.id,
      title,
      document?.id,
      documentType,
      uploadFile,
      cloudUrl,
      notes,
      undefined,
      (percent) => setProgress(percent),
      false,
    );
    console.log("done uploading small file")

    return doc?.id
  }

  const uploadLargeFile = async (uploadFile: File): Promise<number | undefined> => {
    try {
      if (!activeEstate?.id || !user?.id) {
        return
      }

      console.log("uploading large file")
      let docId: number | undefined = undefined

      // Create file metadata in database/laravel
      docId = await createFileInDatabase()
      if (!docId) {
        setErrors(["We were unable to create the file in our system, please try again"])
        return
      }

      // Get presigned s3 url
      const [uploadUrl, s3Key] = await documentsAPI.fetchUploadUrl(activeEstate.id, user.id, docId, uploadFile.name) || [undefined, undefined]
      if (!uploadUrl) {
        setErrors(["couldn't get a cloud url to upload your doc to"])
        return
      }

      // If we haven't set the s3 key for the file in the db yet, do it now
      if (!s3Key) {
      }

      console.log("uploading doc to s3")
      if (!(await documentsAPI.postDocumentToS3(uploadFile, uploadUrl, (percent) => setProgress(percent)))) {
        throw new Error("we couldn't upload your document")
      }

      return docId

    } catch (e) {
      setErrors(["we couldn't get a cloud url to upload your doc to"])
      return
    }
  }

  const isPdf = (document?: UserDocument, documentSrc?: string, uploadedFiles?: FileList): boolean => {
    if (document && document.newestVersion?.ext === "pdf") {
      return true
    }

    if (documentSrc && documentSrc?.toLowerCase().endsWith("pdf")) {
      return true
    }

    if (!!uploadedFiles?.length && uploadedFiles[0].name?.endsWith("pdf")) {
      return true
    }

    return false
  }

  const handleSubmit = async (shouldClose: boolean): Promise<void> => {
    if (!activeEstate?.id || !user?.id) {
      setErrors(["We were unable to create the file in our system, if the problem persists please log out and back in"])
      return
    }

    setProgress(undefined);
    setErrors([]);
    setIsUploading(true)
    setMessage("Submitting...")

    const submitErrors: string[] = [];
    if (!title?.trim()) {
      submitErrors.push("Please enter a document title.");
    }

    if (!documentType) {
      submitErrors.push("Please enter a document type.");
    }

    const uploadFile = uploadedFiles?.item(0) || undefined;
    if (isNew) {
      if (!uploadFile && !cloudUrl) {
        submitErrors.push("Please choose a document to upload.");
      }
    }

    if (submitErrors?.length) {
      setIsUploading(false)
      setMessage("")
      setErrors(submitErrors);
      return;
    }

    let uploaded = false
    try {
      if (uploadFile) {
        if ((uploadFile.size || 0) <= MAX_SMALL_UPLOAD_BYTES) {
          uploaded = !!(await uploadSmallFile(uploadFile))
        } else {
          uploaded = !!(await uploadLargeFile(uploadFile))
        }
      } else {


        console.log("no file to upload, just doing the thing")
        const docId = await documentsAPI.saveDocument(
          activeEstate?.id,
          user?.id,
          title,
          document?.id,
          documentType,
          undefined,
          cloudUrl,
          notes,
          undefined,
          undefined,
          false,
          undefined,
        )
        if (!docId) {
          throw new Error("we didn't receive a response to our upload attempt.  Please try again.")
        }
      }
    } catch (e) {
      console.error((e as any)?.message)
      setIsUploading(false)
      setMessage("")
      setErrors(["Sorry, the file upload failed.  Please try again."]);
      return
    }

    let success = false
    if ((!uploadFile || uploaded)) {
      success = true
    }

    setIsUploading(false)
    if (success) {
      if (shouldClose) {
        setUploadedFiles(undefined);
        setProgress(undefined);
        setMessage("")
      } else {
        setMessage("File uploaded successfully")
      }

      onUpdateDoc?.(shouldClose);
    } else {
      setErrors(["Sorry, the file upload failed.  Please try again."]);
      setProgress(undefined);
      setMessage("")
    }
  };

  const isPDF = isPdf(document, documentSrc, uploadedFiles)
  const previewImage = useMemo(() => {
    if (!documentSrc && (!uploadedFiles || uploadedFiles.length === 0)) {
      return undefined
    }

    return <DocumentThumbnail
      document={document}
      documentSrc={documentSrc}
      style={isPDF ? {
        minHeight: "400px",
      } : undefined}
    />
  }, [documentSrc, uploadedFiles, !!cloudUrl])

  const fileUploadBox = useMemo(() => {
    return <div style={{ opacity: isNew && cloudUrl ? "50%" : "100%" }}>
      <RenderFileUploadBox
        key={document?.id + "_" + documentSrc}
        uploadedFiles={uploadedFiles}
        setErrors={setErrors}
        setUploadedFiles={setUploadedFiles}
        document={document}
        documentSrc={documentSrc}
        previewImage={previewImage}
        onPreviewClick={() => setMode?.(Modes.view)}
      />
    </div>
  }, [documentSrc, uploadedFiles, !!cloudUrl])

  return (<>
    <Grid container spacing={2} style={{ width: "100%", minWidth: "400px" }}>
      <Grid item xs={12} sm={12} md={6} lg={5} xl={5}>
        {fileUploadBox}
      </Grid>
      <Grid item xs={12} sm={12} display={{ xs: "block", sm: "block", md: "none", lg: "none", xl: "none" }} style={{ marginTop: "-1rem" }}>
        {isNew && <div style={{ marginBottom: "1rem" }}>
          or
        </div>}
        {isNew && <TextField
          fullWidth
          label="Upload a file via URL"
          style={{ marginBottom: "1rem" }}
          value={cloudUrl}
          onChange={e => setCloudUrl(e.target.value.toString())}
        />}
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={7} xl={7}>
        <TextField
          fullWidth
          variant="outlined"
          name="title"
          label="Document Title"
          value={title}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setTitle(event.target.value);
          }}
          onFocus={() => setErrors([])}
          style={{ marginBottom: "1rem", marginTop: "1rem" }}
        />
        <DocumentTypeFormControl
          initialDocumentTypeId={documentType}
          onFocus={() => setErrors([])}
          onChange={(documentTypeId: number) => {
            setDocumentType(documentTypeId)
          }}
          includeOnlyOptions={includeOnlyDocumentTypeIds}
        />
        {!isNew && document.version?.cloudUrl &&
          <TextField
            fullWidth
            label="Upload a file via URL"
            style={{ marginBottom: "1rem" }}
            value={cloudUrl}
            minRows={1}
            maxRows={6}
            inputProps={{ maxLength: 10000 }}
            multiline
            onChange={e => setCloudUrl(e.target.value.toString())}
          />}
        <TextField
          variant="outlined"
          name="notes"
          label="Notes"
          style={{ width: "100%", marginBottom: "1rem" }}
          minRows={3}
          maxRows={10}
          inputProps={{ maxLength: 5000 }}
          value={notes}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setNotes(event.target.value);
          }}
          onFocus={() => setErrors([])}
          multiline={true}
        />
      </Grid>
    </Grid>
    {isNew && <Box style={{ marginBottom: "1rem" }} display={{ xs: "none", sm: "none", md: "block", lg: "block", xl: "block" }} >
      or
    </Box>}
    {/* keep as document.version.cloudurl so that if we clear the url it doesn't hide this input box */}
    {isNew && <Grid container spacing={2} style={{ width: "100%", minWidth: "400px" }} display={{ xs: "none", sm: "none", md: "block", lg: "block", xl: "block" }} >
      <Grid item xs={12} sm={12} md={!cloudUrl ? 6 : 12} lg={!cloudUrl ? 5 : 12} xl={!cloudUrl ? 5 : 12}>
        <TextField
          fullWidth
          label="Upload a file via URL"
          style={{ marginBottom: "1rem" }}
          value={cloudUrl}
          onChange={e => setCloudUrl(e.target.value.toString())}
        />
      </Grid>
    </Grid>}
    <Grid container spacing={2} justifyContent="flex-end" wrap="wrap-reverse" style={{ paddingRight: "1rem" }}>
      <Grid item xs={12} sm={12} md={2} lg={2} xl={2}>
        <Button
          onClick={() => (onCancel ? onCancel() : null)}
          variant={"outlined"}
          color="primary"
          style={{ width: "100%" }}
        >
          Cancel
        </Button>
      </Grid>
      <Grid item xs={12} sm={12} md={2} lg={2} xl={2}>
        <Button
          color="primary"
          variant="contained"
          autoFocus
          disabled={isLoading || isUploading}
          style={{ width: "100%", whiteSpace: "nowrap" }}
          onClick={() => handleSubmit(true)}
        >
          {<CircularProgress color="secondary" size="md" style={{ marginRight: "1rem", display: "inline-block" }} />}
          {isNew ? "Upload" : "Update Details"}
          <CircularProgress color="secondary" size="md" style={{ marginLeft: "1rem", visibility: "hidden" }} />
        </Button>
      </Grid>
    </Grid>
    <div style={{ height: "30px", overflow: "hidden" }}>
      {progress && (
        <Grid container style={{ marginTop: "5px" }}>
          <Grid item xs={3} sm={3} md={3} lg={3} xl={3}>
            <Loading />
          </Grid>
          <Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
            <LinearProgress
              variant="determinate"
              value={progress}
              color="secondary"
              sx={{
                height: 16,
                mt: 2,
                mb: 1,
              }}
            />
          </Grid>
        </Grid>
      )}
      {errors?.length
        ? errors.map((error: string) => (
          <Typography color="error" key={error} sx={{ m: 1 }}>
            {error}
          </Typography>
        ))
        : null}
      {message && <Typography color="primary" key={message} sx={{ m: 1 }}>
        {message}
      </Typography>}
      {isSuccessful && (
        <Typography
          color="#9292ff"
          variant={"body1"}
          style={{ marginTop: "10px" }}
        >
          Successful update
        </Typography>
      )}
    </div>
  </>
  );
};

